/**
 * Educators Page
 * 
 * Dedicated page for teachers and schools showcasing how
 * "Where the Brave Things Grow" fits into classroom curriculum.
 * 
 * Sections:
 * 1. Hero - Why teachers love this book
 * 2. Classroom Implementation Guide
 * 3. Curriculum Alignment (CASEL, state standards)
 * 4. Educator testimonials
 * 5. Free resources CTA
 */

import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  Heart, 
  Users, 
  CheckCircle, 
  Clock, 
  BookOpen, 
  GraduationCap,
  Download,
  Target,
  Sparkles
} from "lucide-react";

// Why teachers love this book
const teacherBenefits = [
  {
    icon: Target,
    title: "Aligns with CASEL Standards",
    description: "Directly supports all five core competencies of social-emotional learning."
  },
  {
    icon: Users,
    title: "Circle Time Ready",
    description: "Perfect for whole-class read-alouds with built-in discussion prompts."
  },
  {
    icon: Clock,
    title: "No Prep Required",
    description: "Everything you need is included—just open and read with your class."
  },
  {
    icon: Sparkles,
    title: "Engaging for All Learners",
    description: "Visual, auditory, and kinesthetic elements keep every child involved."
  }
];

// Implementation approaches
const implementations = [
  {
    title: "Whole-Class Reading",
    time: "15-20 minutes",
    description: "Gather students in a circle and read together, using the interactive cues to pause for discussion and practice.",
    icon: Users
  },
  {
    title: "Small Group Activities",
    time: "10-15 minutes",
    description: "Use chapter-specific interactive cues to practice mindfulness, emotional awareness, and other skills in small groups.",
    icon: Heart
  },
  {
    title: "Independent Practice",
    time: "5-10 minutes",
    description: "Children can revisit chapters on their own, practicing the breathing exercises and identifying feelings.",
    icon: BookOpen
  }
];

// CASEL alignment
const caselCompetencies = [
  {
    competency: "Self-Awareness",
    chapter: "Chapter 2: The Colorful Chameleon",
    skill: "Identifying and naming emotions"
  },
  {
    competency: "Self-Management",
    chapter: "Chapter 1: The Still Pond",
    skill: "Practicing mindfulness and calming techniques"
  },
  {
    competency: "Social Awareness",
    chapter: "Chapter 4: The Kindness Meadow",
    skill: "Understanding empathy and kindness"
  },
  {
    competency: "Relationship Skills",
    chapter: "Chapter 6: The Sharing Tree",
    skill: "Practicing sharing and cooperation"
  },
  {
    competency: "Responsible Decision-Making",
    chapter: "Chapter 3: The Brave Bridge",
    skill: "Building courage to face challenges"
  }
];

export default function Educators() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-forest-50 to-golden-50 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-gradient-to-br from-golden-300/20 to-golden-400/15 rounded-full"
            style={{ borderRadius: "60% 40% 30% 70% / 60% 30% 70% 40%" }}
            animate={{
              borderRadius: [
                "60% 40% 30% 70% / 60% 30% 70% 40%",
                "30% 60% 70% 40% / 50% 60% 30% 60%",
                "60% 40% 30% 70% / 60% 30% 70% 40%"
              ]
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Badge className="mb-6 bg-forest-100 text-forest-700 border-forest-200 px-6 py-3 text-base">
                <GraduationCap className="w-5 h-5 mr-2 inline" />
                For Educators
              </Badge>
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Bring Social-Emotional Learning
                <br />
                <span className="text-forest-600">to Your Classroom</span>
              </h1>
              <p className="text-xl lg:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-12">
                A research-backed interactive book designed for preschool and kindergarten 
                educators—ready to use, easy to implement, loved by students.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" className="bg-forest-600 hover:bg-forest-700 text-white px-8 py-6 text-lg" asChild>
                  <Link to="/demo">
                    <BookOpen className="w-6 h-6 mr-3" />
                    Preview the Book
                  </Link>
                </Button>
                <Button size="lg" variant="outline" className="border-forest-600 text-forest-600 hover:bg-forest-50 px-8 py-6 text-lg" asChild>
                  <a href="https://forms.clickup.com/2370929/f/28bbh-75451/2WWNJ2GGXUMT0C1NUZ" target="_blank" rel="noopener noreferrer">
                    <Download className="w-6 h-6 mr-3" />
                    Request Info Packet
                  </a>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Teachers Love It */}
      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Badge className="mb-4 bg-coral-100 text-coral-700 border-coral-200">
                ❤️ Teacher-Approved
              </Badge>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Why Teachers Love This Book
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Designed with busy educators in mind—effective, engaging, and easy to use.
              </p>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {teacherBenefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="border-2 hover:shadow-lg hover:border-forest-300 transition-all duration-300 h-full">
                    <CardContent className="p-8 text-center">
                      <div className="w-16 h-16 bg-forest-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Icon className="w-8 h-8 text-forest-600" />
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-4">
                        {benefit.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {benefit.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Classroom Implementation */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-cream-50 to-forest-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Badge className="mb-4 bg-golden-100 text-golden-700 border-golden-200">
                📚 Implementation Guide
              </Badge>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                How to Use It in Your Classroom
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Flexible implementation options that fit seamlessly into your daily routine.
              </p>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {implementations.map((impl, index) => {
              const Icon = impl.icon;
              return (
                <motion.div
                  key={impl.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="border-2 h-full">
                    <CardContent className="p-8">
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-12 h-12 bg-forest-100 rounded-full flex items-center justify-center">
                          <Icon className="w-6 h-6 text-forest-600" />
                        </div>
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900">
                            {impl.title}
                          </h3>
                          <p className="text-sm text-forest-600 font-medium">
                            <Clock className="w-3 h-3 inline mr-1" />
                            {impl.time}
                          </p>
                        </div>
                      </div>
                      <p className="text-gray-600 leading-relaxed">
                        {impl.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CASEL Alignment */}
      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Badge className="mb-4 bg-forest-100 text-forest-700 border-forest-200">
                ✓ Standards-Aligned
              </Badge>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                CASEL Framework Alignment
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Each chapter directly supports core social-emotional learning competencies.
              </p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <Card className="border-2">
              <CardContent className="p-0">
                <div className="divide-y">
                  {caselCompetencies.map((item, index) => (
                    <div key={item.competency} className="p-8 hover:bg-forest-50/50 transition-colors">
                      <div className="flex items-start gap-6">
                        <div className="w-12 h-12 bg-forest-600 text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold text-lg">
                          {index + 1}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-semibold text-gray-900 mb-2">
                            {item.competency}
                          </h3>
                          <p className="text-forest-600 font-medium mb-2">
                            {item.chapter}
                          </p>
                          <p className="text-gray-600">
                            <CheckCircle className="w-4 h-4 inline mr-2 text-forest-600" />
                            {item.skill}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-golden-50 to-cream-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Badge className="mb-4 bg-coral-100 text-coral-700 border-coral-200">
                💬 Teacher Stories
              </Badge>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                What Educators Are Saying
              </h2>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Card className="border-2 h-full">
                <CardContent className="p-8">
                  <div className="text-golden-600 text-3xl mb-4">"</div>
                  <p className="text-gray-700 leading-relaxed mb-6 italic">
                    This book has transformed our morning circle time. The interactive cues 
                    give my students practical tools they can use throughout the day when 
                    they're feeling overwhelmed or upset.
                  </p>
                  <div className="border-t pt-4">
                    <p className="font-semibold text-gray-900">Sarah M.</p>
                    <p className="text-sm text-gray-600">Pre-K Teacher, 8 years experience</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="border-2 h-full">
                <CardContent className="p-8">
                  <div className="text-golden-600 text-3xl mb-4">"</div>
                  <p className="text-gray-700 leading-relaxed mb-6 italic">
                    I love how each chapter builds on the previous one. By the end of six weeks, 
                    my kindergarteners can identify their emotions and use calming strategies 
                    independently. It's incredible!
                  </p>
                  <div className="border-t pt-4">
                    <p className="font-semibold text-gray-900">Jennifer L.</p>
                    <p className="text-sm text-gray-600">Kindergarten Teacher, 12 years experience</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32" style={{ background: "linear-gradient(135deg, hsl(150 65% 30%) 0%, hsl(45 90% 45%) 100%)" }}>
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              Ready to Bring SEL to Your Classroom?
            </h2>
            <p className="text-xl lg:text-2xl text-cream-100 mb-12 max-w-3xl mx-auto leading-relaxed">
              Request a free educator info packet or preview the book to see how it works.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-white text-forest-600 hover:bg-cream-50 px-8 py-6 text-lg" asChild>
                <Link to="/demo">
                  <BookOpen className="w-6 h-6 mr-3" />
                  Preview the Book
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-forest-600 px-8 py-6 text-lg transition-all" asChild>
                <a href="https://forms.clickup.com/2370929/f/28bbh-75451/2WWNJ2GGXUMT0C1NUZ" target="_blank" rel="noopener noreferrer">
                  <Download className="w-6 h-6 mr-3" />
                  Request Info Packet
                </a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
